'use client';

import * as React from 'react';
import {
  QueryClient,
  QueryClientProvider,
  QueryClientConfig,
  MutationCache,
  QueryCache
} from '@tanstack/react-query';
import { isDebugEnabled } from '@/lib/utils/debug';
/**
 * React Query Provider
 * Provides optimized data fetching with caching, synchronization, and background updates
 */

/**
 * Type guard to check if an error has a status property
 */
function hasStatus(error: unknown): error is { status: number } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  );
}

// ============================================================================
// QUERY CLIENT CONFIGURATION
// ============================================================================

const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Stale time: How long data is considered fresh (5 minutes)
      staleTime: 5 * 60 * 1000,

      // Refetch on window focus in production only
      refetchOnWindowFocus: process.env.NODE_ENV === 'production',

      // Retry failed requests with exponential backoff
      // Note: 401s are handled by the AuthGate (token refresh + retry)
      // before they reach React Query, so no 401 retry needed here.
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (permanent client errors)
        if (hasStatus(error) && error.status >= 400 && error.status < 500) {
          return false;
        }
        // Retry up to 3 times for 5xx and network errors
        return failureCount < 3;
      },

      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Network mode
      networkMode: 'online'
    },
    mutations: {
      // Retry mutations once
      retry: 1,

      // Network mode
      networkMode: 'online'
    }
  },
  // Auth errors (401) are handled by the AuthGate in client.ts before
  // reaching React Query. No auth-specific error handling needed here.
  queryCache: new QueryCache(),
  mutationCache: new MutationCache()
};

// ============================================================================
// QUERY CLIENT SINGLETON
// ============================================================================

let browserQueryClient: QueryClient | undefined = undefined;

function makeQueryClient() {
  return new QueryClient(queryClientConfig);
}

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: use singleton pattern
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export interface QueryProviderProps {
  children: React.ReactNode;
}

const ReactQueryDevtoolsLazy = React.lazy(() =>
  import('@tanstack/react-query-devtools/production').then((mod) => ({
    default: mod.ReactQueryDevtools
  }))
);

export function QueryProvider({ children }: QueryProviderProps) {
  const queryClient = React.useMemo(() => getQueryClient(), []);

  const [showTanstackQueryDebug] = React.useState<boolean>(() => {
    if (isDebugEnabled) return true;
    if (typeof window === 'undefined') return false;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('devtools') || window.sessionStorage.devtools) {
      window.sessionStorage.devtools = 'true';
      return true;
    }
    return false;
  });

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {showTanstackQueryDebug && (
        <React.Suspense fallback={null}>
          <ReactQueryDevtoolsLazy
            initialIsOpen={false}
            buttonPosition="bottom-right"
          />
        </React.Suspense>
      )}
    </QueryClientProvider>
  );
}
